import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import {
  Box,
  Typography,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Stack,
  Button,
  Chip,
  Popover,
  Modal,
} from "@mui/material";
import { defaultStyle } from "components/Modals";
import { getMeteringPointContracts } from "api/resources/meteringPoints";
import { deleteContract } from "../../../api/resources/contracts";
import EditContractModal from "../EditContract";

interface MeteringPointContractsProps {
  id: number;
  setSnackbar: any;
}

const MeteringPointContracts = ({
  id,
  setSnackbar,
}: MeteringPointContractsProps) => {
  const [contracts, setContracts] = useState([]);
  const [editContractModal, setEditContractModal] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const queryMeteringPointContracts = async () => {
    getMeteringPointContracts(id)
      .then((result) => {
        setContracts(result);
      })
      .catch((error) => console.error(error));
  };

  const handleDeleteContract = (contractId: number) => {
    deleteContract(contractId)
      .then(() => {
        queryMeteringPointContracts();
        setSnackbar({
          type: "info",
          message: "Contract deleted",
        });
        handleClose();
      })
      .catch((error) => {
        console.error(error);
        setSnackbar({
          type: "error",
          message: "Error deleting contract",
        });
      });
  };

  const open = Boolean(anchorEl);

  useEffect(() => {
    queryMeteringPointContracts();
  }, [id]);

  return (
    <Box sx={defaultStyle}>
      <Typography id="modal-modal-title" variant="h5" component="h5">
        Metering point contracts
      </Typography>
      <Box mt={2}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="left">Start date</TableCell>
              <TableCell align="left">End date</TableCell>
              <TableCell align="left">Status</TableCell>
              <TableCell align="left">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contracts.map((contract, contractIndex) => {
              const isActive = contract.isActive;

              const inThePast = dayjs(contract.lastDate) < dayjs();

              const inTheFuture = dayjs(contract.firstDate) > dayjs();

              return (
                <TableRow
                  key={"Contract-" + contractIndex}
                  sx={{
                    "&:last-child td, &:last-child th": {
                      border: 0,
                    },
                  }}
                >
                  <TableCell align="left">
                    {dayjs(contract.firstDate).format("DD.MM.YYYY")}
                  </TableCell>
                  <TableCell align="left">
                    {dayjs(contract.lastDate).format("DD.MM.YYYY")}
                  </TableCell>
                  <TableCell align="left">
                    {isActive && (
                      <Chip label="Active" color="success" size="small" />
                    )}
                    {inThePast && (
                      <Chip label="Passed" color="default" size="small" />
                    )}
                    {inTheFuture && (
                      <Chip label="Upcoming" color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="left">
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setEditContractModal(contract.id)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleClick}
                      >
                        Delete
                      </Button>
                      <Popover
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                        slotProps={{
                          paper: {
                            sx: { mt: 1 }, // adds 8px gap
                          },
                        }}
                      >
                        <div style={{ padding: "1rem" }}>
                          <Typography>Are you sure?</Typography>
                          <Stack direction="row" spacing={1} mt={1}>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleDeleteContract(contract.id)}
                            >
                              Yes, delete
                            </Button>
                            <Button size="small" onClick={handleClose}>
                              Cancel
                            </Button>
                          </Stack>
                        </div>
                      </Popover>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      <Modal
        open={!!editContractModal}
        onClose={() => {
          setEditContractModal(null);
        }}
      >
        <Box>
          <EditContractModal
            selectedMeteringPoints={editContractModal}
            setSelectedMeteringPoints={setEditContractModal}
            setOpenModal={setEditContractModal}
            setSnackbar={setSnackbar}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default MeteringPointContracts;
